import { FormEvent, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarPlus,
  CheckCircle2,
  CreditCard,
  Edit3,
  Loader2,
  MessageSquare,
  ShieldCheck,
  UserRound,
  UsersRound,
  WalletCards,
} from 'lucide-react';
import { fetchMindcareBookings } from '@mindcare/shared/api';
import {
  useMindcareAdminTherapists,
  useMindcareAdminWithdrawals,
  useMindcareBarazas,
  useMindcareContentRequests,
  useMindcareJournalPrompts,
  useMindcareResources,
  useUpdateMindcareContentRequest,
  useUpdateMindcareTherapistApproval,
  useUpdateMindcareAdminWithdrawal,
  useUpsertMindcareBaraza,
  useUpsertMindcareJournalPrompt,
  useUpsertMindcareResource,
} from '@mindcare/shared/hooks/useMindcare';
import { useShopContext } from '@mindcare/shared/context/ShopContext';

const initialBaraza = {
  title: '',
  type: 'Live Baraza',
  startsAt: '',
  facilitator: '',
  seats: 'Open registration',
  description: '',
  tags: 'Community, Online, Support',
  waitingRoomUrl: '',
  meetingUrl: '',
  status: 'published',
};

const initialResource = {
  title: '',
  category: 'Guide',
  readTime: '5 min read',
  description: '',
  accent: 'bg-[#ebf5fe] text-[#47615a]',
  contentUrl: '',
  status: 'published',
};

const initialPrompt = {
  title: '',
  mood: 'Reflective',
  excerpt: '',
  prompt: '',
  tags: 'Reflection, Therapy',
  status: 'published',
};

const statCards = [
  { label: 'Therapists', value: (count: number) => String(count), Icon: UsersRound },
  { label: 'Published Barazas', value: (count: number) => String(count), Icon: CalendarPlus },
  { label: 'Clinical approvals', value: () => 'Required', Icon: ShieldCheck },
];

export default function MindCareAdmin() {
  const { backendUrl, adminToken } = useShopContext();
  const base = (backendUrl || 'http://localhost:4000').replace(/\/+$/, '');
  const therapists = useMindcareAdminTherapists(base);
  const barazas = useMindcareBarazas(base);
  const resources = useMindcareResources(base);
  const prompts = useMindcareJournalPrompts(base);
  const contentRequests = useMindcareContentRequests(base);
  const approveTherapist = useUpdateMindcareTherapistApproval(base);
  const withdrawals = useMindcareAdminWithdrawals(base, adminToken);
  const updateWithdrawal = useUpdateMindcareAdminWithdrawal(base, adminToken);
  const upsertBaraza = useUpsertMindcareBaraza(base);
  const upsertResource = useUpsertMindcareResource(base);
  const upsertPrompt = useUpsertMindcareJournalPrompt(base);
  const updateContentRequest = useUpdateMindcareContentRequest(base);
  const bookings = useQuery({
    queryKey: ['mindcare', 'admin', 'bookings'],
    queryFn: () => fetchMindcareBookings(base),
  });
  const [barazaForm, setBarazaForm] = useState(initialBaraza);
  const [resourceForm, setResourceForm] = useState(initialResource);
  const [promptForm, setPromptForm] = useState(initialPrompt);
  const [payoutReferences, setPayoutReferences] = useState<Record<string, string>>({});
  const patientRows = useMemo(
    () => Array.from(new Map((bookings.data || []).map((booking) => [booking.clientEmail, booking])).values()),
    [bookings.data],
  );
  const paidBookings = (bookings.data || []).filter((booking) => booking.paymentStatus === 'paid');
  const pendingPayments = (bookings.data || []).filter((booking) => booking.paymentStatus !== 'paid');

  async function handleBarazaSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await upsertBaraza.mutateAsync({
      ...barazaForm,
      startsAt: new Date(barazaForm.startsAt).toISOString(),
      tags: barazaForm.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    });
    setBarazaForm(initialBaraza);
  }

  async function handleResourceSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await upsertResource.mutateAsync({
      ...resourceForm,
    });
    setResourceForm(initialResource);
  }

  async function handlePromptSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await upsertPrompt.mutateAsync({
      ...promptForm,
      tags: promptForm.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    });
    setPromptForm(initialPrompt);
  }

  return (
    <div className="space-y-8">
      <header className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
        <p className="text-xs font-semibold uppercase text-emerald-700 dark:text-emerald-300">MindCare Admin</p>
        <h1 className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">Therapy operations dashboard</h1>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-darkTextSecondary">
          Approve therapists, monitor patients, review booking payments, and publish Baraza, Resource, and Journal content from one place.
        </p>
      </header>

      <section className="grid gap-5 lg:grid-cols-4">
        {[...statCards, { label: 'Patients', value: () => String(patientRows.length), Icon: UserRound }].map(
          ({ label, value, Icon }, index) => (
            <article key={label} className="rounded-lg border border-gray-200 bg-white p-5 dark:border-darkCard dark:bg-darkCard">
              <Icon className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
              <p className="mt-4 text-xs font-semibold uppercase text-gray-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                {value(index === 0 ? therapists.data?.length || 0 : barazas.data?.length || 0)}
              </p>
            </article>
          ),
        )}
      </section>

      <section id="patients" className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr]">
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <div className="flex items-center gap-3">
            <UsersRound className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Patients</h2>
          </div>
          <div className="mt-5 space-y-3">
            {patientRows.length ? (
              patientRows.map((patient) => (
                <article key={patient.clientEmail} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
                  <p className="font-semibold text-gray-900 dark:text-white">{patient.clientName}</p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">{patient.clientEmail}</p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">{patient.clientPhone || 'No phone saved'}</p>
                </article>
              ))
            ) : (
              <p className="text-sm text-gray-600 dark:text-darkTextSecondary">No patient bookings yet.</p>
            )}
          </div>
        </div>

        <div id="payments" className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Bookings and payments</h2>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <div className="rounded-lg bg-emerald-50 p-4 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-100">
              <p className="text-xs font-semibold uppercase">Paid sessions</p>
              <p className="mt-2 text-3xl font-semibold">{paidBookings.length}</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-4 text-amber-900 dark:bg-amber-500/15 dark:text-amber-100">
              <p className="text-xs font-semibold uppercase">Awaiting payment</p>
              <p className="mt-2 text-3xl font-semibold">{pendingPayments.length}</p>
            </div>
          </div>
          <div className="mt-5 space-y-3">
            {(bookings.data || []).slice(0, 8).map((booking) => (
              <article key={booking.id} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{booking.clientName}</p>
                    <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">
                      {new Date(booking.startsAt).toLocaleString()} | {booking.mode}
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-white">
                    {booking.paymentStatus || 'unpaid'}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Therapist approvals</h2>
        </div>
        <div className="mt-5 space-y-3">
          {therapists.data?.map((therapist) => (
            <article key={therapist.slug} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{therapist.name}</p>
                  <p className="text-sm text-gray-600 dark:text-darkTextSecondary">
                    {therapist.title} | {therapist.city}, {therapist.country}
                  </p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                  {therapist.approvalStatus || 'approved'}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {['approved', 'pending', 'rejected'].map((status) => (
                  <button
                    key={status}
                    className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold dark:border-white/10"
                    type="button"
                    onClick={() =>
                      approveTherapist.mutate({
                        slug: therapist.slug,
                        approvalStatus: status,
                        adminNotes: status === 'approved' ? 'Approved for MindCare publishing.' : '',
                      })
                    }
                  >
                    Set {status}
                  </button>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="therapist-payouts" className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
        <div className="flex items-center gap-3">
          <WalletCards className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Therapist payouts</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">Review withdrawal requests, send the transfer, then record its provider reference.</p>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {withdrawals.data?.length ? withdrawals.data.map((withdrawal) => (
            <article key={withdrawal.id} className="grid gap-4 rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-gray-900 dark:text-white">{withdrawal.therapistName || 'Therapist'}</p>
                  <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold capitalize text-gray-700 dark:bg-white/10 dark:text-white">{withdrawal.status}</span>
                </div>
                <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">{withdrawal.therapistEmail} · {withdrawal.method} · {withdrawal.destination}</p>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{new Intl.NumberFormat('en-KE', { style: 'currency', currency: withdrawal.currency, maximumFractionDigits: 0 }).format(withdrawal.amountCents / 100)}</p>
                {withdrawal.payoutReference ? <p className="mt-1 text-xs text-gray-500">Transfer reference: {withdrawal.payoutReference}</p> : null}
              </div>
              {withdrawal.status === 'requested' ? (
                <div className="flex flex-wrap gap-2">
                  <button className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white" type="button" onClick={() => updateWithdrawal.mutate({ id: withdrawal.id, status: 'approved' })}>Approve</button>
                  <button className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700" type="button" onClick={() => updateWithdrawal.mutate({ id: withdrawal.id, status: 'rejected' })}>Reject</button>
                </div>
              ) : withdrawal.status === 'approved' ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input className="min-w-40 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-white" aria-label={`Transfer reference for ${withdrawal.therapistName || 'therapist'}`} placeholder="Transfer reference" value={payoutReferences[withdrawal.id] || ''} onChange={(event) => setPayoutReferences({ ...payoutReferences, [withdrawal.id]: event.target.value })} />
                  <button className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" type="button" disabled={!payoutReferences[withdrawal.id]?.trim() || updateWithdrawal.isPending} onClick={() => updateWithdrawal.mutate({ id: withdrawal.id, status: 'paid', payoutReference: payoutReferences[withdrawal.id] })}>Record paid</button>
                  <button className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700" type="button" disabled={updateWithdrawal.isPending} onClick={() => updateWithdrawal.mutate({ id: withdrawal.id, status: 'rejected' })}>Reject</button>
                </div>
              ) : null}
            </article>
          )) : <p className="text-sm text-gray-600 dark:text-darkTextSecondary">No therapist payout requests yet.</p>}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={handleBarazaSubmit} className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <div className="flex items-center gap-3">
            <Edit3 className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Add or edit Baraza</h2>
          </div>
          <div className="mt-5 grid gap-4">
            {[
              ['title', 'Title'],
              ['type', 'Type'],
              ['facilitator', 'Facilitator'],
              ['seats', 'Seats label'],
              ['tags', 'Tags'],
              ['waitingRoomUrl', 'Waiting room URL'],
              ['meetingUrl', 'Meeting URL'],
            ].map(([key, label]) => (
              <label key={key} className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
                {label}
                <input
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                  value={barazaForm[key as keyof typeof barazaForm]}
                  onChange={(event) => setBarazaForm({ ...barazaForm, [key]: event.target.value })}
                  required={key !== 'waitingRoomUrl' && key !== 'meetingUrl'}
                />
              </label>
            ))}
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Starts at
              <input
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                type="datetime-local"
                value={barazaForm.startsAt}
                onChange={(event) => setBarazaForm({ ...barazaForm, startsAt: event.target.value })}
                required
              />
            </label>
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Description
              <textarea
                className="mt-2 min-h-28 w-full rounded-lg border border-gray-200 bg-white p-4 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={barazaForm.description}
                onChange={(event) => setBarazaForm({ ...barazaForm, description: event.target.value })}
                required
              />
            </label>
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Status
              <select
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={barazaForm.status}
                onChange={(event) => setBarazaForm({ ...barazaForm, status: event.target.value })}
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>
          <button className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white" type="submit">
            {upsertBaraza.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save Baraza
          </button>
        </form>

        <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Published Barazas</h2>
          <div className="mt-5 space-y-3">
            {barazas.data?.map((baraza) => (
              <article key={baraza.slug} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
                <p className="font-semibold text-gray-900 dark:text-white">{baraza.title}</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">
                  {new Date(baraza.startsAt).toLocaleString()} | {baraza.type}
                </p>
                <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-darkTextSecondary">{baraza.description}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                  {baraza.waitingRoomUrl ? <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-800">Waiting room</span> : null}
                  {baraza.meetingUrl ? <span className="rounded-full bg-sky-50 px-3 py-1 text-sky-800">Meeting link</span> : null}
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">{baraza.status || 'published'}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={handleResourceSubmit} className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Add or edit Resource</h2>
          </div>
          <div className="mt-5 grid gap-4">
            {[
              ['title', 'Title'],
              ['category', 'Category'],
              ['readTime', 'Read time'],
              ['contentUrl', 'Content URL'],
            ].map(([key, label]) => (
              <label key={key} className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
                {label}
                <input
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                  value={resourceForm[key as keyof typeof resourceForm]}
                  onChange={(event) => setResourceForm({ ...resourceForm, [key]: event.target.value })}
                  required={key !== 'contentUrl'}
                />
              </label>
            ))}
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Accent class
              <input
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={resourceForm.accent}
                onChange={(event) => setResourceForm({ ...resourceForm, accent: event.target.value })}
              />
            </label>
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Description
              <textarea
                className="mt-2 min-h-28 w-full rounded-lg border border-gray-200 bg-white p-4 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={resourceForm.description}
                onChange={(event) => setResourceForm({ ...resourceForm, description: event.target.value })}
                required
              />
            </label>
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Status
              <select
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={resourceForm.status}
                onChange={(event) => setResourceForm({ ...resourceForm, status: event.target.value })}
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </label>
          </div>
          <button className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white" type="submit">
            {upsertResource.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save Resource
          </button>
        </form>

        <form onSubmit={handlePromptSubmit} className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <div className="flex items-center gap-3">
            <Edit3 className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Add or edit Journal prompt</h2>
          </div>
          <div className="mt-5 grid gap-4">
            {[
              ['title', 'Title'],
              ['mood', 'Mood'],
              ['excerpt', 'Excerpt'],
            ].map(([key, label]) => (
              <label key={key} className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
                {label}
                <input
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                  value={promptForm[key as keyof typeof promptForm]}
                  onChange={(event) => setPromptForm({ ...promptForm, [key]: event.target.value })}
                  required
                />
              </label>
            ))}
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Prompt
              <textarea
                className="mt-2 min-h-28 w-full rounded-lg border border-gray-200 bg-white p-4 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={promptForm.prompt}
                onChange={(event) => setPromptForm({ ...promptForm, prompt: event.target.value })}
                required
              />
            </label>
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Tags
              <input
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={promptForm.tags}
                onChange={(event) => setPromptForm({ ...promptForm, tags: event.target.value })}
              />
            </label>
            <label className="text-sm font-semibold text-gray-700 dark:text-darkTextSecondary">
              Status
              <select
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 font-normal dark:border-white/10 dark:bg-white/5 dark:text-white"
                value={promptForm.status}
                onChange={(event) => setPromptForm({ ...promptForm, status: event.target.value })}
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </label>
          </div>
          <button className="mt-5 inline-flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white" type="submit">
            {upsertPrompt.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save Prompt
          </button>
        </form>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Published resources</h2>
          <div className="mt-5 space-y-3">
            {resources.data?.map((resource) => (
              <article key={resource.slug} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
                <p className="font-semibold text-gray-900 dark:text-white">{resource.title}</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">
                  {resource.category} | {resource.readTime}
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-darkTextSecondary">{resource.description}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Journal prompts</h2>
          <div className="mt-5 space-y-3">
            {prompts.data?.map((prompt) => (
              <article key={prompt.slug} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
                <p className="font-semibold text-gray-900 dark:text-white">{prompt.title}</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">{prompt.mood}</p>
                <p className="mt-2 text-sm text-gray-600 dark:text-darkTextSecondary">{prompt.excerpt}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-darkCard dark:bg-darkCard">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Therapist requests</h2>
          <div className="mt-5 space-y-3">
            {contentRequests.data?.map((request) => (
              <article key={request.id} className="rounded-lg border border-gray-100 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-gray-900 dark:text-white">{request.title}</p>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 dark:bg-white/10 dark:text-white">
                    {request.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-600 dark:text-darkTextSecondary">
                  {request.section} | {request.requesterEmail}
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-darkTextSecondary">{request.summary}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['approved', 'implemented', 'rejected'].map((status) => (
                    <button
                      key={status}
                      type="button"
                      className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold dark:border-white/10"
                      onClick={() => updateContentRequest.mutate({ id: request.id, status })}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
